import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBack } from '../hooks';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import api from '../api';
import { C, cs } from '../styles/common';

const MUTED  = C.muted;
const BORDER = C.border;
const machineName = m => m?.display_name || m?.name || '—';

function SpecRow({ label, value }) {
  if (value == null || value === '' || value === false) return null;
  return (
    <div style={{ display: 'flex', gap: '8px', justifyContent: 'space-between',
      padding: '4px 0', borderBottom: `1px solid ${BORDER}22`, fontSize: '12px' }}>
      <span style={{ color: MUTED }}>{label}</span>
      <span style={{ color: C.text, fontWeight: '500', textAlign: 'right' }}>{String(value)}</span>
    </div>
  );
}

function MachineCard({ machine, isDirector, onRecommissioned }) {
  const { confirm } = useToast();
  const [open, setOpen]         = useState(false);
  const [working, setWorking]   = useState(false);

  function recommission() {
    confirm({
      message: `Recommission ${machineName(machine)}? It will return to Available status.`,
      confirmLabel: 'Recommission',
      onConfirm: () => {
        setWorking(true);
        api.put(`/api/v1/machines/${machine.id}`, { status: 'available' })
          .then(() => onRecommissioned(machine.id))
          .catch(() => setWorking(false));
      },
    });
  }

  return (
    <div style={{ background: C.surface, border: `1px solid ${BORDER}`, borderRadius: '12px', overflow: 'hidden' }}>
      <div style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}
        onClick={() => setOpen(o => !o)}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '14px', fontWeight: '700', color: C.text }}>{machineName(machine)}</span>
            <span style={{ fontSize: '9px', fontWeight: '700', background: '#1f2937', color: '#9ca3af',
              border: `1px solid ${BORDER}`, borderRadius: '4px', padding: '1px 6px',
              textTransform: 'uppercase', letterSpacing: '0.05em' }}>Decommissioned</span>
          </div>
          <div style={{ fontSize: '11px', color: MUTED, marginTop: '3px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            {machine.maker && <span>{machine.maker}{machine.model ? ` · ${machine.model}` : ''}</span>}
            {machine.tonnes && <span>{machine.tonnes}t</span>}
            {machine.year && <span>{machine.year}</span>}
            {machine.serial_no && <span>S/N {machine.serial_no}</span>}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {isDirector && (
            <button onClick={e => { e.stopPropagation(); recommission(); }} disabled={working}
              style={{ background: '#166534', color: '#86efac', border: 'none', borderRadius: '6px',
                padding: '5px 12px', fontSize: '11px', fontWeight: '700', cursor: 'pointer', flexShrink: 0 }}>
              {working ? '...' : 'Recommission'}
            </button>
          )}
          <i className={`fa-solid fa-chevron-${open ? 'up' : 'down'}`} style={{ fontSize: '11px', color: MUTED }} />
        </div>
      </div>

      {open && (
        <div style={{ borderTop: `1px solid ${BORDER}`, padding: '14px 18px',
          display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0 24px' }}>
          <SpecRow label="Clamping force"    value={machine.tonnes ? `${machine.tonnes} t` : null} />
          <SpecRow label="Clamp kN"          value={machine.clamp_kn ? `${machine.clamp_kn} kN` : null} />
          <SpecRow label="Screw diameter"    value={machine.screw_mm ? `${machine.screw_mm} mm` : null} />
          <SpecRow label="Barrel volume"     value={machine.barrel_cm3 ? `${machine.barrel_cm3} cm³` : null} />
          <SpecRow label="Max inj. pressure" value={machine.max_inj_bar ? `${machine.max_inj_bar} bar` : null} />
          <SpecRow label="Drive type"        value={machine.drive_type} />
          <SpecRow label="Clamp type"        value={machine.clamp_type} />
          <SpecRow label="Tie bar H"         value={machine.tie_bar_h_mm ? `${machine.tie_bar_h_mm} mm` : null} />
          <SpecRow label="Tie bar V"         value={machine.tie_bar_v_mm ? `${machine.tie_bar_v_mm} mm` : null} />
          <SpecRow label="Power"             value={machine.kw ? `${machine.kw} kW` : null} />
          <SpecRow label="Controller"        value={machine.controller_brand ? `${machine.controller_brand}${machine.controller_model ? ` ${machine.controller_model}` : ''}` : null} />
          <SpecRow label="Site"              value={machine.site} />
        </div>
      )}
    </div>
  );
}

function MouldCard({ mould, isDirector, onRecommissioned }) {
  const { confirm } = useToast();
  const [open, setOpen]       = useState(false);
  const [working, setWorking] = useState(false);

  function recommission() {
    confirm({
      message: `Recommission ${mould.ref}? It will return to Active status.`,
      confirmLabel: 'Recommission',
      onConfirm: () => {
        setWorking(true);
        api.put(`/api/v1/moulds/${mould.id}`, { status: 'active' })
          .then(() => onRecommissioned(mould.id))
          .catch(() => setWorking(false));
      },
    });
  }

  return (
    <div style={{ background: C.surface, border: `1px solid ${BORDER}`, borderRadius: '12px', overflow: 'hidden' }}>
      <div style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}
        onClick={() => setOpen(o => !o)}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '13px', fontWeight: '700', color: C.text }}>{mould.ref}</span>
            {mould.name && <span style={{ fontSize: '12px', color: MUTED }}>· {mould.name}</span>}
            <span style={{ fontSize: '9px', fontWeight: '700', background: '#1f2937', color: '#9ca3af',
              border: `1px solid ${BORDER}`, borderRadius: '4px', padding: '1px 6px',
              textTransform: 'uppercase', letterSpacing: '0.05em' }}>Decommissioned</span>
          </div>
          <div style={{ fontSize: '11px', color: MUTED, marginTop: '3px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            {mould.cavities && <span>{mould.cavities} cavit{mould.cavities === 1 ? 'y' : 'ies'}</span>}
            {mould.gate_type && <span>{mould.gate_type} gate</span>}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {isDirector && (
            <button onClick={e => { e.stopPropagation(); recommission(); }} disabled={working}
              style={{ background: '#166534', color: '#86efac', border: 'none', borderRadius: '6px',
                padding: '5px 12px', fontSize: '11px', fontWeight: '700', cursor: 'pointer', flexShrink: 0 }}>
              {working ? '...' : 'Recommission'}
            </button>
          )}
          <i className={`fa-solid fa-chevron-${open ? 'up' : 'down'}`} style={{ fontSize: '11px', color: MUTED }} />
        </div>
      </div>

      {open && (
        <div style={{ borderTop: `1px solid ${BORDER}`, padding: '14px 18px',
          display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0 24px' }}>
          <SpecRow label="Cavities"         value={mould.cavities} />
          <SpecRow label="Gate type"        value={mould.gate_type} />
          <SpecRow label="Shot weight"      value={mould.sw_g ? `${mould.sw_g} g` : null} />
          <SpecRow label="Part weight"      value={mould.pw_g ? `${mould.pw_g} g` : null} />
          <SpecRow label="Runner weight"    value={mould.rw_g ? `${mould.rw_g} g` : null} />
          <SpecRow label="Projected area"   value={mould.pa_cm2 ? `${mould.pa_cm2} cm²` : null} />
          <SpecRow label="Max width"        value={mould.wmax_mm ? `${mould.wmax_mm} mm` : null} />
          <SpecRow label="Gate width"       value={mould.gw_mm ? `${mould.gw_mm} mm` : null} />
          <SpecRow label="Gate depth"       value={mould.gd_mm ? `${mould.gd_mm} mm` : null} />
          <SpecRow label="Ejector stroke"   value={mould.ej_stroke_mm ? `${mould.ej_stroke_mm} mm` : null} />
          <SpecRow label="Ejector pulses"   value={mould.ej_pulses} />
        </div>
      )}
    </div>
  );
}

export default function Equipment() {
  const navigate = useNavigate();
  const goBack   = useBack('/admin');
  const { isDirector } = useAuth();
  const [machines, setMachines] = useState([]);
  const [moulds,   setMoulds]   = useState([]);
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/api/v1/machines').catch(() => ({ data: [] })),
      api.get('/api/v1/moulds').catch(() => ({ data: [] })),
    ]).then(([m, mo]) => {
      setMachines((m.data || []).filter(x => x.status === 'archived'));
      setMoulds((mo.data || []).filter(x => x.status === 'archived'));
      setLoading(false);
    });
  }, []);

  function removeMachine(id) { setMachines(ms => ms.filter(m => m.id !== id)); }
  function removeMould(id)   { setMoulds(ms => ms.filter(m => m.id !== id)); }

  return (
    <div style={cs.page}>
      {/* Breadcrumb */}
      <div style={cs.breadcrumb}>
        <button onClick={goBack} style={cs.backBtn}>
          <i className="fa-solid fa-chevron-left" style={{ fontSize: '10px' }} /> Back
        </button>
        <span style={cs.sep}>·</span>
        <button onClick={() => navigate('/admin/history?view=retired')} style={cs.backBtn}>History</button>
        <span style={cs.sep}>·</span>
        <span style={cs.crumb}>Equipment</span>
      </div>

      <div style={cs.pageHeader}>
        <div>
          <h1 style={cs.pageTitle}>Equipment</h1>
          <p style={cs.pageSub}>Decommissioned machines and moulds — read-only record</p>
        </div>
      </div>

      {loading ? (
        <div style={cs.center}>Loading…</div>
      ) : machines.length === 0 && moulds.length === 0 ? (
        <div style={{ ...cs.center, flexDirection: 'column', gap: '10px' }}>
          <i className="fa-solid fa-industry" style={{ fontSize: '28px', color: MUTED }} />
          <span style={{ color: MUTED, fontSize: '13px' }}>No decommissioned equipment on record.</span>
          <span style={{ color: MUTED, fontSize: '11px' }}>
            Set a machine or mould status to "Decommissioned" in Admin → Machines or Admin → Moulds.
          </span>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>

          {/* Machines */}
          {machines.length > 0 && (
            <section>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
                <i className="fa-solid fa-gear" style={{ fontSize: '13px', color: MUTED }} />
                <span style={{ fontSize: '12px', fontWeight: '700', color: MUTED,
                  textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                  Machines ({machines.length})
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {machines.map(m => <MachineCard key={m.id} machine={m} isDirector={isDirector()} onRecommissioned={removeMachine} />)}
              </div>
            </section>
          )}

          {/* Moulds */}
          {moulds.length > 0 && (
            <section>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
                <i className="fa-solid fa-box" style={{ fontSize: '13px', color: MUTED }} />
                <span style={{ fontSize: '12px', fontWeight: '700', color: MUTED,
                  textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                  Moulds ({moulds.length})
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {moulds.map(m => <MouldCard key={m.id} mould={m} isDirector={isDirector()} onRecommissioned={removeMould} />)}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}
